import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Flame, MapPin, Clock, CreditCard } from "lucide-react";
import TopBar from "../components/TopBar";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useTheme } from "../contexts/ThemeContext";
import LandingChipCarousel from "../components/LandingChipCarousel";

export default function Landing() {
  const { user } = useAuth();
  const { heroImageUrl, heroImageUrlDark, landingHeadingLine1, landingHeadingLine2, landingChips } = useCompany();
  const { theme } = useTheme();
  // En dark mode usamos el hero "obscuro" (fondo negro / wordmark claro);
  // si no se configuró uno, caemos al claro. Cada slot tiene su propio
  // default en backend.
  const activeHero = theme === "dark" ? (heroImageUrlDark || heroImageUrl) : heroImageUrl;
  const primaryHref = user
    ? (user.role === "super_admin" ? "/superadmin" : user.role === "company_admin" ? "/admin" : "/app")
    : "/app";
  // Clients always see "Pedir ahora" (their landing goes straight to /app where they order).
  // Staff members see "Ir a mi panel" since they don't place orders.
  const primaryLabel = user && (user.role === "super_admin" || user.role === "company_admin")
    ? "Ir a mi panel"
    : "Pedir ahora";
  return (
    <div className="min-h-screen bg-white dark:bg-[#0b0b0d]">
      <TopBar />
      <main>
        <section className="max-w-6xl mx-auto px-4 pt-16 pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            {/* Imagen hero de plataforma (editable por super_admin).
                Aparece arriba del eyebrow "Gas a domicilio".
                Ratio protegido: usamos `max-h-*` (no `h-*`) para que el
                preflight de Tailwind (`max-width:100%; height:auto`)
                pueda escalar ambas dimensiones proporcionalmente cuando
                el viewport se reduce. Si le ponemos `h-*` explícito el
                alto queda fijo mientras el ancho se recorta → distorsión.
                `object-contain` es un cinturón+tirantes extra. */}
            {activeHero && (
              <img
                src={activeHero}
                alt="Gas2GO"
                className="max-h-16 sm:max-h-20 md:max-h-24 w-auto max-w-full h-auto object-contain mb-6 select-none"
                draggable={false}
                data-testid="landing-hero-image"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-neutral-500">Gas a domicilio</p>
            <h1 className="mt-4 text-5xl md:text-7xl font-extrabold font-display leading-[0.95] tracking-tighter" data-testid="landing-heading">
              {landingHeadingLine1}<br />
              <span className="text-company">{landingHeadingLine2}</span>
            </h1>
            <p className="mt-6 text-neutral-600 text-lg max-w-md">
              Pide cilindros o gas a granel. Pago al entregar o en línea.
              Elige el punto exacto en el mapa y listo.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" asChild className="h-14 px-8 text-base bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl" data-testid="hero-cta-btn">
                <Link to={primaryHref}>{primaryLabel} <ArrowRight size={18} className="ml-2" /></Link>
              </Button>
              {!user && (
                <Button size="lg" variant="outline" asChild className="h-14 px-6 text-base rounded-xl" data-testid="cta-login">
                  <Link to="/login">Iniciar sesión</Link>
                </Button>
              )}
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-lg">
              {[
                { Icon: Flame, t: "2 canales", s: "Cilindros o granel" },
                { Icon: Clock, t: "Ahora o agenda", s: "Tú decides" },
                { Icon: MapPin, t: "Pin exacto", s: "Google Maps" },
                { Icon: CreditCard, t: "Pago flexible", s: "Efectivo o en línea" },
              ].map(({ Icon, t, s }, i) => (
                <div key={i}>
                  <Icon size={20} className="text-company" />
                  <p className="mt-2 font-semibold text-sm">{t}</p>
                  <p className="text-xs text-neutral-500">{s}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <LandingChipCarousel chips={landingChips} />
          </div>
        </section>
      </main>
    </div>
  );
}
